import React, { useState, useEffect, useCallback } from "react";
import {
  Bus,
  UserPlus
} from "lucide-react";
import {
  Button,
  Input,
  Select,
  Table,
  Badge,
  EmptyState,
  PageHeader
} from "../../components/ui";
import AssignmentModal from "../../components/transport/AssignmentModal";
import {
  getAcademicYears,
  getAcademicYearsPortal,
  getClasses,
  getClassesPortal,
  getSections,
  getStudents,
  getStudentsPortal,
  getAllAssignments,
  getAllAssignmentsPortal
} from "../../api/transport/transportApi";
import {
  useToast
} from "../../context/ToastContext";

/**
 * Dedicated page to pick a student (filter by class / section / search)
 * and assign transport.
 */
const AssignTransportPage = ({ isPortal = false }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    academicYearId: "",
    classId: "",
    sectionId: "",
    search: "",
  });

  const [yearOptions, setYearOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);

  const toast = useToast();

  // Map of studentId -> active assignment (for current year)
  const [assignedMap, setAssignedMap] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editData, setEditData] = useState(null);

  // Load years + classes once
  useEffect(() => {
    (async () => {
      try {
        const [yRes, cRes] = await Promise.all([
          isPortal ? getAcademicYearsPortal() : getAcademicYears(),
          isPortal ? getClassesPortal() : getClasses(),
        ]);
        const years = yRes.data || yRes || [];
        const classes = cRes.data || cRes || [];
        setYearOptions(
          years.map((y) => ({
            value: y._id,
            label: y.yearName || y.name || String(y._id),
          }))
        );
        setClassOptions(
          classes.map((c) => ({
            value: c._id,
            label: c.className || c.name || String(c._id),
          }))
        );
        const active = years.find((y) => y.isActive);
        if (active) {
          setFilters((p) => ({ ...p, academicYearId: active._id }));
        } else if (years[0]) {
          setFilters((p) => ({ ...p, academicYearId: years[0]._id }));
        }
      } catch {
        /* ignore */
      }
    })();
  }, [isPortal]);

  // Sections when class changes
  useEffect(() => {
    if (!filters.classId) {
      setSectionOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await getSections({ classId: filters.classId });
        const list = res.data || res || [];
        setSectionOptions(
          list.map((s) => ({
            value: s._id,
            label: s.sectionName || s.name || String(s._id),
          }))
        );
      } catch {
        setSectionOptions([]);
      }
    })();
  }, [filters.classId]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.classId) params.classId = filters.classId;
      if (filters.sectionId) params.sectionId = filters.sectionId;
      if (filters.search) params.search = filters.search;

      const res = isPortal
        ? await getStudentsPortal(params)
        : await getStudents(params);
      let list = res.data || res || [];

      // Client-side search fallback
      if (filters.search) {
        const q = filters.search.toLowerCase();
        list = list.filter((s) => {
          const name = `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase();
          return (
            name.includes(q) ||
            (s.admissionNo || "").toLowerCase().includes(q) ||
            (s.rollNumber || "").toLowerCase().includes(q)
          );
        });
      }

      setStudents(list);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load students");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [isPortal, filters.classId, filters.sectionId, filters.search]);

  // Load active assignments for year (to show status on student rows)
  const fetchAssignments = useCallback(async () => {
    if (!filters.academicYearId) {
      setAssignedMap({});
      return;
    }
    try {
      const params = {
        academicYearId: filters.academicYearId,
        status: "ACTIVE",
      };
      if (filters.classId) params.classId = filters.classId;
      if (filters.sectionId) params.sectionId = filters.sectionId;

      const res = isPortal
        ? await getAllAssignmentsPortal(params)
        : await getAllAssignments(params);
      const list = res.data || res || [];
      const map = {};
      list.forEach((a) => {
        const sid = a.studentId?._id || a.studentId;
        if (sid) map[String(sid)] = a;
      });
      setAssignedMap(map);
    } catch {
      setAssignedMap({});
    }
  }, [
    isPortal,
    filters.academicYearId,
    filters.classId,
    filters.sectionId,
  ]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const openAssign = (student) => {
    const existing = assignedMap[String(student._id)];
    setSelectedStudent(student);
    setEditData(existing || null);
    setModalOpen(true);
  };

  const columns = [
    {
      key: "name",
      header: "Student",
      render: (row) => (
        <div>
          <div className="font-medium text-neutral-900 dark:text-neutral-100">
            {row.firstName} {row.lastName}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            {row.admissionNo || "—"}
            {row.rollNumber ? ` · Roll ${row.rollNumber}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "class",
      header: "Class / Section",
      render: (row) => {
        const cls =
          row.classId?.className ||
          row.classId?.classCode ||
          row.className ||
          "—";
        const sec =
          row.sectionId?.sectionName || row.sectionName || "—";
        return (
          <span>
            {cls} / {sec}
          </span>
        );
      },
    },
    {
      key: "transportMode",
      header: "Mode",
      render: (row) => (
        <Badge
          variant={
            row.transportMode === "SCHOOL_TRANSPORT" ? "primary" : "default"
          }
        >
          {(row.transportMode || "PARENTS").replace(/_/g, " ")}
        </Badge>
      ),
    },
    {
      key: "assignment",
      header: "Transport status",
      render: (row) => {
        const a = assignedMap[String(row._id)];
        if (!a) {
          return <Badge variant="default">Not assigned</Badge>;
        }
        const v = a.vehicleId || {};
        return (
          <div>
            <Badge variant="success">Assigned</Badge>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              {v.name || "Vehicle"} · {a.pincode}
              {a.monthlyCharge != null
                ? ` · ₹${Number(a.monthlyCharge).toFixed(0)}`
                : ""}
            </div>
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "Action",
      render: (row) => {
        const a = assignedMap[String(row._id)];
        return (
          <Button
            size="sm"
            variant={a ? "secondary" : "primary"}
            leftIcon={<UserPlus className="h-3.5 w-3.5" />}
            onClick={() => openAssign(row)}
          >
            {a ? "Update" : "Assign"}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <PageHeader
        title="Assign Student Transport"
        description="Filter students by class and section, then assign a vehicle and pincode"
      />

      
      

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Select
          label="Academic year"
          options={yearOptions}
          value={filters.academicYearId}
          onChange={(v) => setFilters((p) => ({ ...p, academicYearId: v }))}
          placeholder="Select year"
        />
        <Select
          label="Class"
          options={[{ label: "All classes", value: "" }, ...classOptions]}
          value={filters.classId}
          onChange={(v) =>
            setFilters((p) => ({ ...p, classId: v, sectionId: "" }))
          }
          placeholder="All classes"
          searchable
        />
        <Select
          label="Section"
          options={[{ label: "All sections", value: "" }, ...sectionOptions]}
          value={filters.sectionId}
          onChange={(v) => setFilters((p) => ({ ...p, sectionId: v }))}
          placeholder="All sections"
          disabled={!filters.classId}
          searchable
        />
        <Input
          label="Search"
          placeholder="Name, admission no, roll no..."
          value={filters.search}
          onChange={(e) =>
            setFilters((p) => ({ ...p, search: e.target.value }))
          }
        />
      </div>

      <Table
        columns={columns}
        data={students}
        isLoading={loading}
        emptyState={
          <EmptyState
            icon={Bus}
            title="No students found"
            description="Adjust class, section or search filters to find students."
          />
        }
      />

      <AssignmentModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedStudent(null);
          setEditData(null);
        }}
        onSuccess={() => {
          toast.success(
            editData ? "Assignment updated." : "Transport assigned successfully."
          );
          fetchStudents();
          fetchAssignments();
        }}
        editData={editData}
        student={selectedStudent}
        academicYearId={filters.academicYearId}
        academicYearOptions={yearOptions}
        isPortal={isPortal}
      />
    </div>
  );
};

export default AssignTransportPage;
